import React, { useEffect, useMemo, useState, useCallback, lazy, Suspense, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../constants/api.js";
import { showSuccess, showError } from "../utils/toast.js";
import { smartBack } from "../constants/nav";
import { useTheme } from "../context/ThemeContext.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import ModalOrSheet from "../components/ui/ModalOrSheet.jsx";
import { Checkbox, Input } from "../components/forms/shared/FormAtoms.jsx";
import {
  detailPageHeaderNavClassName,
  detailPageHeaderNavActionsClassName,
  successButtonLoadingClassName,
} from "../design/tokens.js";
import { ROLE_ADMIN, ROLE_GL, ROLE_PL, ROLE_SEK } from "../constants/roles.js";
import { useAcademyContextEvent } from "../hooks/useAcademyContextEvent.js";

// ───────────────── Lazy-Blocks ─────────────────
const ProfileInfoBlock = lazy(() => import("../components/profil/InfoBlock.jsx"));
const RapportBlock = lazy(() => import("../components/profil/RapportBlock.jsx"));
const ProfilAbwesenheitBlock = lazy(() => import("../components/profil/AbwesenheitBlock.jsx"));
const WeiterbildungenBlock = lazy(() => import("../components/profil/WeiterbildungsBlock.jsx"));
import SmallPasskeyButton from "../components/profil/SmallPasskeyButton.jsx";
import PersoenlicherCodeModal from "../components/profil/PersoenlicherCodeModal.jsx";

// ───────────────── Skeletons (UI wie Übersicht) ─────────────────
function LineSkeleton({
  h = 16,
  className = ""
}) {
  return <div className={`bg-muted rounded-xl border border-default animate-pulse ${className}`} style={{
    height: h
  }} />;
}
function PanelSkeleton({
  lines = 3
}) {
  return <div className="rounded-2xl border border-default bg-surface p-4 md:p-5">
      <div className="space-y-3">
        {Array.from({
        length: lines
      }).map((_, i) => <LineSkeleton key={i} h={14 + i % 2 * 6} />)}
      </div>
    </div>;
}
function Block({
  children,
  fallbackLines = 3
}) {
  return <Suspense fallback={<PanelSkeleton lines={fallbackLines} />}>{children}</Suspense>;
}

// ───────────────── Utils ─────────────────
const norm = v => String(v ?? "").toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const okFlag = d => d?.ok === true || d?.success === true || d?.erfolg === true;
export default function Profil() {
    const nav = useNavigate();
  const location = useLocation();
  useAcademyContextEvent("visit_profil");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);

  // Scroll zu Anker (Abwesenheit etc.)
  useEffect(() => {
    if (loading) return;
    let scrollTimer = null;
    const scrollTarget =
      location.state?.scrollTo && location.state.scrollTo !== "unterschrift-code"
        ? location.state.scrollTo
        : location.hash
          ? location.hash.replace("#", "")
          : null;
    if (scrollTarget) {
      const el = document.getElementById(scrollTarget);
      if (el) {
        scrollTimer = setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    }
    return () => {
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [location.hash, location.state?.scrollTo, loading]);
  const setTitle = useCallback(payload => {
    const nm = payload?.user?.name || "Profil";
    document.title = `${nm} • Profil • WireTrack`;
  }, []);
  const loadMe = useCallback(async (signal) => {
    abortRef.current?.abort?.();
    const ctrl = signal instanceof AbortController ? signal : new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setLoadError(false);
    try {
      const {
        data
      } = await api.get("/profile/me", {
        signal: ctrl.signal
      });
      if (!mountedRef.current) return;
      if (!data || typeof data !== "object" || !data.user) throw new Error("Ungültige Antwort");
      setMe(data);
      setTitle(data);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (err?.response?.status === 401) {
        showError("Bitte erneut einloggen.");
        nav("/login");
        return;
      }
      setMe(null);
      setLoadError(true);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [nav, setTitle]);
  useEffect(() => {
    mountedRef.current = true;
    const ctrl = new AbortController();
    loadMe(ctrl);
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort?.();
      ctrl.abort();
    };
    // loadMe ist stabil ([nav, setTitle]); bewusst nur beim Mount, um Request-Loops zu vermeiden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rollen-Flags (GL/Admin erben PL/Büro)
  const {
    isGL,
    isPLBuero
  } = useMemo(() => {
    const role = norm(me?.user?.rolle);
    const rid = Number(me?.user?.rolle_id ?? 99);
    const gl = role === "geschaeftsleitung" || role === "geschäftsleitung" || role === "admin" || rid === ROLE_ADMIN || rid === ROLE_GL;
    const plbuero = gl || role === "projektleitung" || role === "pl" || role === "buero" || role === "büro" || rid === ROLE_PL || rid === ROLE_SEK;
    return {
      isGL: gl,
      isPLBuero: plbuero
    };
  }, [me]);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (location.state?.openPersoenlicherCode) {
      setCodeOpen(true);
    }
  }, [loading, location.state?.openPersoenlicherCode]);

  const pageGrad = "bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const retryLoad = () => {
    const ctrl = new AbortController();
    loadMe(ctrl);
  };

  // ───────────────── Loading / Weiterleitung (z. B. 401) ─────────────────
  if (loading || (!me && !loadError)) {
    return <div className={`min-h-screen w-full ${pageGrad}`}>
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-4 md:py-6 space-y-3 md:space-y-4">
          <div className="rounded-2xl px-4 py-3 mb-3 border app-frame">
            <div className={`h-6 w-40 rounded animate-pulse ${"bg-white/10"}`} />
          </div>
          <PanelSkeleton lines={4} />
          <PanelSkeleton lines={3} />
          <PanelSkeleton lines={3} />
        </div>
      </div>;
  }
  if (!me) {
    return <div className={`min-h-screen w-full ${pageGrad}`}>
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-6">
          <div className="rounded-2xl p-5 border app-frame">
            <h1 className="text-lg font-semibold text-blue-100/95">Profil konnte nicht geladen werden</h1>
            <p className="mt-2 text-sm text-blue-200/80 leading-relaxed max-w-xl">
              Die Profildaten sind momentan nicht verfügbar. Bitte prüfe die Verbindung und versuche es erneut.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName} onClick={() => nav("/")}>
                Zum Dashboard
              </AppButton>
              <AppButton type="button" variant="primary" size="lg" onClick={retryLoad}>
                Erneut laden
              </AppButton>
            </div>
          </div>
        </div>
      </div>;
  }

  // ───────────────── Seite (Design wie Übersicht) ─────────────────
  return <div className={`min-h-screen w-full ${pageGrad}`}>
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-6">
        {/* Header – gleiche Nav-Buttons wie Mängel/Service-Detail */}
        <div className="rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 mb-3 sm:mb-4 border transition-all app-frame">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg sm:text-2xl font-bold tracking-tight text-primary">
                Mein Profil
              </h1>
              <p className="mt-0.5 sm:mt-1 text-sm truncate text-secondary">{me?.user?.name || ""}</p>
            </div>

            <div className={`${detailPageHeaderNavActionsClassName} mt-2 sm:mt-0`}>
              <SmallPasskeyButton
                onDone={() => {
                  const ctrl = new AbortController();
                  loadMe(ctrl);
                }}
              />
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                title="Passwort ändern"
                className={detailPageHeaderNavClassName}
                onClick={() => setPwdOpen(true)}
              >
                <span className="md:hidden">Passwort</span>
                <span className="hidden md:inline">Passwort ändern</span>
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                title={me.user?.can_sign_maengel ? "Fachliche Unterschrift (Code und PNG)" : "Persönlicher Code"}
                className={detailPageHeaderNavClassName}
                onClick={() => setCodeOpen(true)}
              >
                <span className="md:hidden">{me.user?.can_sign_maengel ? "Unterschrift" : "Code"}</span>
                <span className="hidden md:inline">
                  {me.user?.can_sign_maengel ? "Unterschrift" : "Persönlicher Code"}
                </span>
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => smartBack(nav, ["/", "/dashboard", "/meine-aufgaben"])}
              >
                Zurück
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="lg"
                className={detailPageHeaderNavClassName}
                onClick={() => nav("/")}
              >
                Dashboard
              </AppButton>
            </div>
          </div>
        </div>

        {/* Inhalte */}
        <div className="grid grid-cols-1 gap-3 md:gap-4">
          <Block fallbackLines={4}>
            <ProfileInfoBlock user={me.user} onUpdated={() => {
            const ctrl = new AbortController();
            loadMe(ctrl);
          }} />
          </Block>

          <Block fallbackLines={3}>
            <RapportBlock userId={me.user?.id} />
          </Block>

          <div id="abwesenheit">
            <Block fallbackLines={4}>
              <ProfilAbwesenheitBlock userId={me.user?.id} initialYear={new Date().getFullYear()} isGL={isGL} isPLBuero={isPLBuero} />
            </Block>
          </div>

          <Block fallbackLines={3}>
            <WeiterbildungenBlock userId={me.user?.id} />
          </Block>
        </div>

        <div className="h-6 md:h-8" />
      </div>

      {pwdOpen ? (
        <ChangePasswordModal
          onClose={() => setPwdOpen(false)}
          onSuccess={() => {
            setPwdOpen(false);
            showSuccess("Passwort aktualisiert");
          }}
        />
      ) : null}

      {codeOpen ? (
        <PersoenlicherCodeModal
          open
          user={me.user}
          onClose={() => setCodeOpen(false)}
          onUpdated={() => {
            const ctrl = new AbortController();
            loadMe(ctrl);
          }}
        />
      ) : null}
    </div>;
}

// ───────────────── Passwort-Modal ─────────────────
function ChangePasswordModal({
  onClose,
  onSuccess
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    current: "",
    next: "",
    confirm: "",
    show: false
  });
  const update = k => e => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const validate = () => {
    if (!form.current) return "Bitte aktuelles Passwort eingeben.";
    const pw = form.next.trim();
    if (pw.length < 10) return "Mindestens 10 Zeichen.";
    const hits = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((n, re) => n + (re.test(pw) ? 1 : 0), 0);
    if (hits < 3) return "Mindestens 3 von 4: Klein, Gross, Zahl, Sonderzeichen.";
    if (form.next === form.current) return "Neues Passwort darf nicht wie das alte sein.";
    if (form.next !== form.confirm) return "Bestätigung stimmt nicht mit dem neuen Passwort überein.";
    return null;
  };
  const submit = async e => {
    e?.preventDefault?.();
    const err = validate();
    if (err) {
      showError(err);
      return;
    }
    try {
      setSubmitting(true);
      const {
        data
      } = await api.post("/auth/change_password", {
        current_password: form.current,
        new_password: form.next
      });
      if (!okFlag(data)) throw new Error("Änderung fehlgeschlagen");
      onSuccess?.();
    } catch (ex) {
      const msg = ex?.response?.data?.message || ex?.message || "Passwortänderung fehlgeschlagen";
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <ModalOrSheet
      open
      onClose={onClose}
      title="Passwort ändern"
      maxWidth="sm"
      footer={
        <div className="flex flex-col max-md:gap-2 sm:flex-row sm:justify-end gap-2">
          <AppButton type="button" variant="secondary" onClick={onClose} className="max-md:min-h-[44px]">
            Abbrechen
          </AppButton>
          <AppButton
            type="submit"
            form="profil-change-password-form"
            variant="success"
            disabled={submitting}
            className={`max-md:min-h-[44px] ${submitting ? successButtonLoadingClassName : ""}`}
          >
            {submitting ? "Speichere…" : "Speichern"}
          </AppButton>
        </div>
      }
    >
        <p className="mb-4 text-sm text-muted">Bitte aktuelles Passwort bestätigen und ein neues vergeben.</p>

        <form id="profil-change-password-form" onSubmit={submit} className="space-y-3">
          <div>
            <label className={`block text-xs mb-1 ${"text-blue-200/70"}`}>Aktuelles Passwort</label>
            <Input type={form.show ? "text" : "password"} className="rounded-lg" autoComplete="current-password" value={form.current} onChange={update("current")} required aria-required="true" />
          </div>

          <div>
            <label className={`block text-xs mb-1 ${"text-blue-200/70"}`}>Neues Passwort</label>
            <Input type={form.show ? "text" : "password"} className="rounded-lg" autoComplete="new-password" value={form.next} onChange={update("next")} required minLength={10} aria-required="true" />
          </div>

          <div>
            <label className={`block text-xs mb-1 ${"text-blue-200/70"}`}>Neues Passwort bestätigen</label>
            <Input type={form.show ? "text" : "password"} className="rounded-lg" autoComplete="new-password" value={form.confirm} onChange={update("confirm")} required minLength={10} aria-required="true" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-secondary pt-1">
            <Checkbox
              checked={form.show}
              onChange={e => setForm(f => ({ ...f, show: e.target.checked }))}
              aria-label="Passwort anzeigen"
            />
            <span>Passwort anzeigen</span>
          </label>
        </form>
    </ModalOrSheet>
  );
}